import { Country } from 'country-state-city'
import React, { ReactElement, useMemo, useState } from 'react'
import styled from 'styled-components'
import DateSelector from './DateSelector'
import SearchMultiselect from './SearchMultiselect'
import { RADOM_COLORS } from '../util/Constants'
import { nYearsAgo, SANCTIONED_COUNTRIES, simpleEmailValidation, splitDateTime, ZERO_POPULATION_TERRITORIES } from '../util/Util'
import { PrimaryButton } from './Button'
import { emptyTravelRuleFields, TravelRuleFields, TravelRuleFieldsCollected } from './Checkout'
import { InputLabel, TextInput } from './Input'

const TravelRuleFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

// To avoid accepting implausible dates of birth, the customer is required to enter
// a date that puts their age between 16 and 110 years old
// The choice of these ages is entirely arbitrary!
const MIN_CUSTOMER_AGE_YEARS: number = 16
const MAX_CUSTOMER_AGE_YEARS: number = 110
const youngestPermittedDateOfBirth: Date = nYearsAgo(MIN_CUSTOMER_AGE_YEARS)
const oldestPermittedDateOfBirth: Date = nYearsAgo(MAX_CUSTOMER_AGE_YEARS)

const Wrapper = styled.div<{}>`flex-grow: 1;`

interface IProps {
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  onTravelRuleFieldsComplete: (trFields: TravelRuleFields) => void
}

function generateInputLabel(labelName: string): ReactElement {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '15px' }}>
    <InputLabel>{labelName}</InputLabel>
  </div>
}

function fromPartialTravelRuleFields(trFields?: Partial<TravelRuleFields>): TravelRuleFields {
  return !trFields
    ? emptyTravelRuleFields()
    : {
      countryOfResidence: trFields.countryOfResidence ?? "",
      dateOfBirth: trFields.dateOfBirth ?? "",
      emailAddress: trFields.emailAddress ?? "",
      name: trFields.name ?? "",
    }
}

// ---------------------------------------------------------------------------------------------------------------------
const TravelRuleFieldsWidget = (props: IProps): React.ReactElement => {
  // The list of countries must not include:
  // * Sanctioned countries
  // * Territories with zero population
  const countries = useMemo(() => Country
    .getAllCountries()
    .filter(c => !SANCTIONED_COUNTRIES.has(c.name) && !ZERO_POPULATION_TERRITORIES.has(c.name)
    ), [])
  const [collectedTravelRuleFields, setCollectedTravelRuleFields] = useState(emptyTravelRuleFields())
  const [errorMessage, setErrorMessage] = useState<string>()

  return <Wrapper style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    {
      errorMessage && errorMessage.length > 0 &&
      <div style={{
        backgroundColor: RADOM_COLORS.ERROR,
        padding: 15,
        borderRadius: 5,
        marginBottom: 20,
        color: 'white',
        overflowWrap: 'break-word'
      }}>
        {errorMessage}
      </div>
    }

    {
      !props.travelRuleFieldsCollected.nameCollected &&
      <TravelRuleFieldsRow key="missingName">
        {generateInputLabel("Name")}
        {
          <TextInput
            key='trName'
            value={collectedTravelRuleFields.name}
            placeholder={'Enter name'}
            required={true}
            onInput={el => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.name = (el.target as HTMLInputElement).value
              setCollectedTravelRuleFields(newTrFields)
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      !props.travelRuleFieldsCollected.emailAddressCollected &&
      <TravelRuleFieldsRow key="missingEmailAddress">
        {generateInputLabel("Email address")}
        {
          <TextInput
            key='trEmailAddress'
            value={collectedTravelRuleFields.emailAddress}
            placeholder={'Enter email address'}
            required={true}
            onInput={el => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.emailAddress = (el.target as HTMLInputElement).value.toLowerCase()
              setCollectedTravelRuleFields(newTrFields)
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      !props.travelRuleFieldsCollected.dateOfBirthCollected &&
      <TravelRuleFieldsRow key="missingDateOfBirth">
        {generateInputLabel("Date of birth")}
        {
          <DateSelector
            key='trDateOfBirth'
            startDate={oldestPermittedDateOfBirth}
            endDate={youngestPermittedDateOfBirth}
            value={collectedTravelRuleFields.dateOfBirth}
            onChange={date => {
              if (date) {
                const newTrFields = { ...collectedTravelRuleFields }
                newTrFields.dateOfBirth = splitDateTime(date).datePart
                setCollectedTravelRuleFields(newTrFields)
              }
            }}
            placeholder="Select date of birth"
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      !props.travelRuleFieldsCollected.countryOfResidenceCollected &&
      <TravelRuleFieldsRow key="missingCountryOfResidence">
        {generateInputLabel("Country")}
        {
          <SearchMultiselect
            key='trCountryOfResidence'
            isCountrySelect={true}
            itemArray={countries.map(c => c.name)}
            selectedItems={[collectedTravelRuleFields.countryOfResidence]}
            setSelectedItems={country => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.countryOfResidence = country[0] as string
              setCollectedTravelRuleFields(newTrFields)
            }}
            keyExtractor={c => c as string}
            labelExtractor={c => c as string}
            isSingleSelect={true}
            placeholder='Select country'
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      <PrimaryButton
        style={{ background: "black", width: '100%', marginTop: '25px' }}
        onClick={() => {
          const errorMsgs: string[] = []

          if (!props.travelRuleFieldsCollected.countryOfResidenceCollected && !collectedTravelRuleFields.countryOfResidence) {
            errorMsgs.push("Country of residence")
          }
          if (!props.travelRuleFieldsCollected.dateOfBirthCollected && !collectedTravelRuleFields.dateOfBirth) {
            errorMsgs.push("Date of birth")
          }
          if (!props.travelRuleFieldsCollected.emailAddressCollected &&
            (!collectedTravelRuleFields.emailAddress || !simpleEmailValidation(collectedTravelRuleFields.emailAddress))) {
            errorMsgs.push("Email address")
          }
          if (!props.travelRuleFieldsCollected.nameCollected && !collectedTravelRuleFields.name) {
            errorMsgs.push("Name")
          }

          // Are any of the required fields still missing?
          if (errorMsgs.length > 0) {
            setErrorMessage(
              `Please enter ${errorMsgs.length === 1 ? 'a valid value' : 'valid values'} for ${errorMsgs.join(', ')}`
            )
          } else {
            props.onTravelRuleFieldsComplete(collectedTravelRuleFields)
            setErrorMessage('')
          }
        }}
      >
        Continue
      </PrimaryButton>
    }
  </Wrapper >
}

export default TravelRuleFieldsWidget
